import { inject, Injectable } from '@angular/core';
import {
  ApiService,
  ParcelsWithProperties,
  SurveyMarkDto,
} from '@core/api/angular';
import { getFeatureById } from '@core/api/angular/operations/get-feature-by-id';
import { getGeneralInfo } from '@core/api/angular/operations/get-general-info';
import { getHistoricalInformationById } from '@core/api/angular/operations/get-historical-information-by-id';
import { getHistoricalInformationDocument } from '@core/api/angular/operations/get-historical-information-document';
import { getParcels } from '@core/api/angular/operations/get-parcels';
import { getPlaceNameListById } from '@core/api/angular/operations/get-place-name-list-by-id';
import { getRoadsById } from '@core/api/angular/operations/get-roads-by-id';
import { getSurveyMark } from '@core/api/angular/operations/get-survey-mark';
import { identifySurveyLabel } from '@core/api/angular/operations/identify-survey-label';
import { chunk } from '@lassi/util';
import { GeoJSON } from 'leaflet';
import {
  defaultIfEmpty,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
} from 'rxjs';
import { DownloadService } from './download.service';
import { GEOSERVER_CRS, IMapFeature } from './feature.model';
import {
  IFeatureData,
  IHistoricalInformation,
  IPropertyData,
  IRoadData,
} from './models';
import {
  TransformCrownStatusPipe,
  TransformZLevelPipe,
  YesNoPipe,
} from './pipes';
import {
  GeographicCoordsPipe,
  LatLngPipe,
  MGAPipe,
  PointPipe,
} from './pipes/coordinates.pipe';

@Injectable({
  providedIn: 'root',
})
export class IdentifyService {
  private readonly PROPERTY_REQUEST_CHUNK_SIZE = 100;

  private apiService = inject(ApiService);
  private csvService = inject(DownloadService);

  identifyGeneralInformation(coords: IMapFeature['coords']) {
    return this.apiService
      .invoke(getGeneralInfo, {
        coordinates: [coords.x.toString(), coords.y.toString()],
      })
      .pipe(map((data) => data.general_info));
  }

  identifyParcels(ids: number[]): Observable<ParcelsWithProperties[]> {
    return forkJoin(
      chunk(ids, this.PROPERTY_REQUEST_CHUNK_SIZE).map((ids) =>
        this.apiService
          .invoke(getParcels, {
            parcel_pfi: ids
              .map((id) => id.toString())
              .join(',') as unknown as string[],
          })
          .pipe(map((data) => data.parcels)),
      ),
    ).pipe(
      defaultIfEmpty([]),
      map((results) => results.flat()),
    );
  }

  identifySurveyMark(id: number): Observable<SurveyMarkDto> {
    return this.apiService.invoke(getSurveyMark, {
      id,
    });
  }

  identifySurveyLabel(id: string) {
    return this.apiService.invoke(identifySurveyLabel, {
      id,
    });
  }

  getHistoricalInfo(historyId: number): Observable<IHistoricalInformation> {
    return this.apiService
      .invoke(getHistoricalInformationById, {
        historyId,
      })
      .pipe(
        map(({ detail, corrDocList }) => ({
          ...detail,
          documents: corrDocList.map((doc) => {
            const url = new URL(
              this.apiService.rootUrl + getHistoricalInformationDocument.PATH,
              window.location.origin,
            );
            url.searchParams.set('id', doc.corroborating_document_id);
            return {
              link: url.toString(),
              name: doc.original_file_name ?? 'Document',
            };
          }),
        })),
      );
  }

  identifyFeature(id: number): Observable<IFeatureData> {
    return forkJoin([
      this.apiService.invoke(getFeatureById, {
        featureId: id,
      }),
      this.apiService.invoke(getPlaceNameListById, {
        featureId: id,
      }),
    ]).pipe(
      map(([{ historical_information, ...rest }, placeNames]) => ({
        ...rest,
        feature_id: id,
        historicalInformation: historical_information,
        geometry: rest.geometry as GeoJSON.Point,
        crs: GEOSERVER_CRS,
        placeNames: placeNames.place_name_list,
      })),
    );
  }

  identifyRoad(id: number): Observable<IRoadData> {
    return this.apiService
      .invoke(getRoadsById, {
        roadId: id,
      })
      .pipe(
        map(({ historical_information, ...rest }) => ({
          ...rest,
          historicalInformation: historical_information,
          geometry: rest.geometry as GeoJSON.LineString,
          crs: GEOSERVER_CRS,
        })),
      );
  }
  private readonly CROWN_DESC_TYPE = '14';

  downloadPropertyCSV(
    propertyData: IPropertyData,
    coords: IMapFeature['coords'],
  ) {
    const { general, parcels } = propertyData;

    const latLng = new LatLngPipe().transform(coords);
    const gda = new GeographicCoordsPipe().transform(latLng);

    const mga = new MGAPipe().transform(latLng);
    const mgaText = new PointPipe().transform(mga.point);

    const crownStatusPipe = new TransformCrownStatusPipe();
    const zLevelPipe = new TransformZLevelPipe();
    const yesNoPipe = new YesNoPipe();

    try {
      const csvData = parcels
        .flatMap((parcel) =>
          (parcel.properties ?? [{}]).map((property) => {
            const isCrownDescription =
              parcel.desc_type === this.CROWN_DESC_TYPE;
            const parcelZLevel = zLevelPipe.transform(parcel.z_level);
            const parcelType =
              (isCrownDescription ? 'Crown Description' : 'Lot / Plan') +
              (parcel.z_level === 'G' ? '' : ' (' + parcelZLevel.name + ')');

            return {
              LGA: general?.lga_name,
              LGA_CODE: general?.lga_code,
              Locality: general?.locality_name,
              Parish: general?.parish_name,
              'Parish Code': general?.parish_code,
              Township: general?.township_name,
              'Township Code': general?.township_code,
              'Parcel Type': parcelType,
              Status: crownStatusPipe.transform(parcel?.crown_status),
              Lot: parcel?.lot_number,
              Plan: parcel?.plan_number,
              Allotment: parcel?.allotment,
              Subdivision: parcel?.subdivision,
              'Parcel Further Description': parcel?.further_description,
              SPI: parcel?.spi,
              'SPI Code': parcel?.spi_code,
              PFI: parcel?.parcel_pfi,
              'View PFI': parcel?.parcel_view_pfi,
              'Description Type': parcel?.desc_type,
              'Is Multi': parcel?.multi && yesNoPipe.transform(parcel.multi),
              'Is Road': parcel?.road && yesNoPipe.transform(parcel.road),
              'Planning Zone': parcel?.planning_zone,
              'Planning Overlay': parcel?.planning_overlay,
              'Property LGA': property?.property_lga_code,
              'Property Number': property?.property_propnum,
              Address: property?.ezi_address,
              'Property PFI': property?.property_pfi,
              'Multi-assessment': property?.property_multi_assessment,
              Area: property?.property_area,
              MGA2020: `${mgaText} (${mga.name})`,
              GDA2020: gda,
            };
          }),
        )
        .filter(Boolean);

      const filename = this.csvService.generateCsvFileName(
        'IdentifyPropertyExport',
      );
      this.csvService.downloadCsv({ data: csvData }, filename, {
        header: true,
        skipEmptyLines: true,
      });
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  }

  private async makeFeatureCSV(featureData: IFeatureData) {
    const { placeNames, place_map, historicalInformation } = featureData;

    const detailedHistoricalInformation = await firstValueFrom(
      forkJoin(
        historicalInformation.map((hi) =>
          this.getHistoricalInfo(Number(hi.id)).pipe(
            map((details) =>
              this.getCSVForHistoricalInformation({
                ...details,
                display_title: hi.display_title,
              }),
            ),
          ),
        ),
      ).pipe(defaultIfEmpty([] as IHistoricalInformation[])),
    );

    const placeNameDataList = placeNames.map((placeName) => ({
      Municipality: place_map?.municipality,
      'Name ID': placeName.place_name_id,
      'Feature Name': placeName.place_name,
      'Feature Name Status': placeName.status_desc,
      'Feature Type Code': placeName.feature_type_code,
      'Feature Type': placeName.feature_type,
      Longitude: place_map?.longitude,
      Latitude: place_map?.latitude,
      'Feature ID': place_map?.place_id,
      Gender: placeName.gender,
      'Aboriginal Origins': placeName.aboriginal_origins,
      'Traditional Owner Group': placeName.traditional_owner,
      'Gazette Published Date': placeName.gazette_published_date,
      'Gazette Reference': placeName.gazette_reference,
      'Gazette URL': placeName.gazette_url,
    }));

    return detailedHistoricalInformation.length
      ? detailedHistoricalInformation.flatMap((hi) =>
          placeNameDataList.map((placeName) => ({
            ...placeName,
            ...hi,
          })),
        )
      : placeNameDataList;
  }

  async downloadFeatureCSV(featureData: IFeatureData) {
    try {
      const mergedData = await this.makeFeatureCSV(featureData);

      const filename = this.csvService.generateCsvFileName('Places');
      this.csvService.downloadCsv({ data: mergedData }, filename, {
        header: true,
        skipEmptyLines: true,
      });
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  }

  private getCSVForHistoricalInformation(
    hi: IHistoricalInformation & { display_title: string },
  ) {
    return {
      'Historical information': hi.information,
      'Origin Language': hi.origin_language,
      'Australian Indigenous Language': hi.origin_language,
      'ANZAC related name': hi.anzac_related_name,
      'Date First Recorded Day': hi.date_first_recorded_day,
      'Date First Recorded Month': hi.date_first_recorded_month,
      'Date First Recorded Year': hi.date_first_recorded_year,
      Source: hi.source_type,
      Author: hi.author,
      Title: hi.title,
      'Date Published Day': hi.date_published_day,
      'Date Published Month': hi.date_published_month,
      'Date Published Year': hi.date_published_year,
      Publisher: hi.publisher,
      'Place of Publication': hi.place_of_publication,
      'Page(s)': hi.pages,
      Editor: hi.editor,
      'Chapter Title': hi.chapter_title,
      'Journal Name': hi.journal_name,
      Issue: hi.issue,
      Year: hi.year,
      Newspaper: hi.newspaper_name,
      'File Number': hi.file_number,
      'Site Owner': hi.site_owner,
      'Page Title': hi.page_title,
      'Site Title': hi.site_title,
      URL: hi.url,
      Location: hi.location,
      'Type of Artefact': hi.type_of_artefact,
      Cartographer: hi.cartographer,
      Scale: hi.scale,
      Informant: hi.informant,
      'Tape Title': hi.tape_title,
      'Tape Number': hi.tape_number,
      'Municipal Name': hi.municipal_name,
      'Endorsed by Council': hi.endorsed_by_council,
      'Council Minutes': hi.council_minutes,
      'Organisation Name': hi.organisation_name,
      'Endorsed by GPNAP': hi.endorsed_by_gpnap,
    };
  }
}
